import { NavLink } from 'react-router-dom'
import { CountBadge } from './Badge.jsx'

/**
 * Two levels of tabs, never three. Audit: the underline tab bar was copy-pasted
 * into 5 layouts and had drifted; second-level tabs came in 4 styles; the
 * evaluation pages had a THIRD level styled heavier than the second.
 *
 *   <Tabs>     level 1 — sections of a page or layout. Underline.
 *   <SubTabs>  level 2 — views inside one section. Small pills.
 *   A third level is a design smell: use a <Segmented> filter instead.
 *
 * items: [{ key, label, to?, end?, badge?, badgeTone? ('alert' | 'count'), badgeUnknown?, icon?, title? }]
 *   with `to`  → router links (the URL owns the active tab — Back works, links can be shared)
 *   without    → controlled by value / onChange
 * Tabs that the user may not open are LEFT OUT of `items`, never shown disabled.
 */
export function Tabs({ items, value, onChange, sticky = false, className = '' }) {
  return (
    <div className={`border-b border-slate-200 ${sticky ? 'sticky top-0 z-20 bg-[#fafbfc]/95 backdrop-blur' : ''} ${className}`}>
      <nav className="flex gap-1 overflow-x-auto no-scrollbar -mb-px" role="tablist">
        {items.map((it) => {
          const inner = (active) => (
            <>
              {it.icon && <it.icon size={15} strokeWidth={2} />}
              <span>{it.label}</span>
              <CountBadge n={it.badge} tone={it.badgeTone} unknown={it.badgeUnknown} />
              <span className={`absolute left-2 right-2 bottom-0 h-[2px] rounded-full ${active ? 'bg-brand-600' : 'bg-transparent'}`} />
            </>
          )
          const cls = (active) => `relative inline-flex items-center gap-1.5 px-3 py-2.5 text-[14px] font-medium whitespace-nowrap transition-colors ${active ? 'text-brand-700' : 'text-slate-500 hover:text-slate-800'}`
          return it.to ? (
            <NavLink key={it.key} to={it.to} end={it.end} role="tab" title={it.title} className={({ isActive }) => cls(isActive)}>
              {({ isActive }) => inner(isActive)}
            </NavLink>
          ) : (
            <button key={it.key} type="button" role="tab" title={it.title} aria-selected={value === it.key} onClick={() => onChange?.(it.key)} className={cls(value === it.key)}>
              {inner(value === it.key)}
            </button>
          )
        })}
      </nav>
    </div>
  )
}

export function SubTabs({ items, value, onChange, className = '' }) {
  return (
    <nav className={`flex flex-wrap gap-1.5 ${className}`} role="tablist">
      {items.map((it) => {
        const cls = (active) => `inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${active ? 'bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'}`
        const inner = <>{it.icon && <it.icon size={14} />}{it.label}<CountBadge n={it.badge} tone={it.badgeTone} unknown={it.badgeUnknown} /></>
        return it.to ? (
          <NavLink key={it.key} to={it.to} end={it.end} role="tab" title={it.title} className={({ isActive }) => cls(isActive)}>{inner}</NavLink>
        ) : (
          <button key={it.key} type="button" role="tab" title={it.title} aria-selected={value === it.key} onClick={() => onChange?.(it.key)} className={cls(value === it.key)}>{inner}</button>
        )
      })}
    </nav>
  )
}
